interface CacheEntry<T> {
  data: T
  timestamp: number
  version: string
}

interface CacheConfig {
  key: string
  ttl: number
  version: string
}

export class LocalStorageCache {
  private config: CacheConfig

  constructor(config: CacheConfig) {
    this.config = config
  }

  get<T>(): T | null {
    try {
      const cached = localStorage.getItem(this.config.key)
      if (!cached) return null

      const entry: CacheEntry<T> = JSON.parse(cached) as CacheEntry<T>

      if (this.isExpired(entry.timestamp)) {
        this.remove()
        return null
      }

      if (entry.version !== this.config.version) {
        this.remove()
        return null
      }

      return entry.data
    } catch (error) {
      console.error('Failed to get cached data:', error)
      this.remove()
      return null
    }
  }

  set<T>(data: T): void {
    try {
      const entry: CacheEntry<T> = {
        data,
        timestamp: Date.now(),
        version: this.config.version
      }

      localStorage.setItem(this.config.key, JSON.stringify(entry))
    } catch (error) {
      console.error('Failed to set cached data:', error)
      if (error instanceof DOMException && error.code === 22) {
        this.clearOldCaches()
        try {
          const retryEntry: CacheEntry<T> = {
            data,
            timestamp: Date.now(),
            version: this.config.version
          }
          localStorage.setItem(this.config.key, JSON.stringify(retryEntry))
        } catch (retryError) {
          console.error('Failed to set cached data after retry:', retryError)
        }
      }
    }
  }

  remove(): void {
    try {
      localStorage.removeItem(this.config.key)
    } catch (error) {
      console.error('Failed to remove cached data:', error)
    }
  }

  hasValidCache(): boolean {
    return this.get() !== null
  }

  getCacheAge(): number | null {
    try {
      const cached = localStorage.getItem(this.config.key)
      if (!cached) return null

      const entry: CacheEntry<unknown> = JSON.parse(cached) as CacheEntry<unknown>
      return Date.now() - entry.timestamp
    } catch (error) {
      return null
    }
  }

  private isExpired(timestamp: number): boolean {
    return Date.now() - timestamp > this.config.ttl
  }

  private clearOldCaches(): void {
    try {
      const keysToRemove: string[] = []

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key?.startsWith('mbue_cache_')) {
          try {
            const cached = localStorage.getItem(key)
            if (cached) {
              const entry: CacheEntry<unknown> = JSON.parse(cached) as CacheEntry<unknown>
              if (Date.now() - entry.timestamp > 7 * 24 * 60 * 60 * 1000) {
                keysToRemove.push(key)
              }
            }
          } catch (error) {
            keysToRemove.push(key)
          }
        }
      }

      keysToRemove.forEach((key) => localStorage.removeItem(key))
    } catch (error) {
      console.error('Failed to clear old caches:', error)
    }
  }

  clearAllCaches(): void {
    try {
      const keysToRemove: string[] = []

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key?.startsWith('mbue_cache_')) {
          keysToRemove.push(key)
        }
      }

      keysToRemove.forEach((key) => localStorage.removeItem(key))
    } catch (error) {
      console.error('Failed to clear all caches:', error)
    }
  }
}

export const navigationCacheConfig: CacheConfig = {
  key: 'mbue_cache_navigation_data',
  ttl: 20 * 60 * 1000, // 20 minutes
  version: '1.0.0'
}

export const userCacheConfig: CacheConfig = {
  key: 'mbue_cache_user_data',
  ttl: 30 * 60 * 1000, // 30 minutes
  version: '1.0.0'
}

export const projectsCacheConfig: CacheConfig = {
  key: 'mbue_cache_projects_data',
  ttl: 1 * 60 * 1000, // 20 minutes
  version: '1.0.0'
}

export const navigationCache = new LocalStorageCache(navigationCacheConfig)
export const userCache = new LocalStorageCache(userCacheConfig)
export const projectsCache = new LocalStorageCache(projectsCacheConfig)
